/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef } from 'react';
import {
  Button,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
} from '@chakra-ui/react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import ImageUpload from '@/components/common/ImageUpload';
import { AddressFieldWithMap } from '@/components/common/AddressFieldWithMap';
import Asterisk from '@/components/common/Asterisk';
import { useGoogleMaps } from '@/hooks/useGoogleMaps';

type FormData = {
  name: string;
  email: string;
  telephone: string;
  company_address?: string;
  certificate_image?: File | string | null;
  pan_vat_certificate_image?: File | string | null;
};

const vendorValidationSchema = yup.object().shape({
  name: yup.string().required('Shop name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  telephone: yup.string().required('Business contact is required'),
  company_address: yup.string().optional(),
  certificate_image: yup.mixed().nullable().optional(),
  pan_vat_certificate_image: yup.mixed().nullable().optional(),
}) as yup.ObjectSchema<FormData>;

type IEditModalProps = {
  isOpen: boolean;
  onClose: () => void;
  handleCancel: () => void;
  handleUpdate: (data: FormData) => void;
  initialData: FormData & {
    certificate_image_url?: string | null;
    pan_vat_certificate_image_url?: string | null;
  };
};

const EditModalContent: React.FC<IEditModalProps> = ({
  isOpen,
  onClose,
  handleCancel,
  handleUpdate,
  initialData,
}) => {
  const methods = useForm<FormData>({
    resolver: yupResolver(vendorValidationSchema),
    defaultValues: {
      name: '',
      email: '',
      telephone: '',
      company_address: '',
      certificate_image: null,
      pan_vat_certificate_image: null,
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
    setValue,
  } = methods;

  const isInitialLoad = useRef(true);

  useEffect(() => {
    if (isOpen && initialData && isInitialLoad.current) {
      setValue('name', initialData.name || '');
      setValue('email', initialData.email || '');
      setValue('telephone', initialData.telephone || '');
      setValue('company_address', initialData.company_address || '');
      // Don't reset image fields if they have been changed by user
      setValue('certificate_image', null);
      setValue('pan_vat_certificate_image', null);
      isInitialLoad.current = false;
    }
  }, [isOpen, initialData, setValue]);

  useEffect(() => {
    if (!isOpen) {
      isInitialLoad.current = true; // Reset for next modal open
    }
  }, [isOpen]);

  const onSubmit = (data: FormData) => {
    const submitData: any = {
      name: data.name, // Keep it as 'name' here since handleUpdate expects it
      email: data.email,
      telephone: data.telephone,
      company_address: data.company_address || null,
    };

    // Only include images if they are actual File objects (user uploaded new ones)
    // Don't include the field at all if no new file was selected
    if (data.certificate_image && data.certificate_image instanceof File) {
      submitData.certificate_image = data.certificate_image;
    }
    if (data.pan_vat_certificate_image && data.pan_vat_certificate_image instanceof File) {
      submitData.pan_vat_certificate_image = data.pan_vat_certificate_image;
    }

    handleUpdate(submitData);
  };

  const { isLoaded: isGoogleMapsLoaded } = useGoogleMaps(isOpen);

  const handleClose = () => {
    reset();
    isInitialLoad.current = true;
    onClose();
  };

  const handleCancelWithReset = () => {
    reset();
    isInitialLoad.current = true;
    handleCancel();
  };

  return (
    <FormProvider {...methods}>
      <Modal isOpen={isOpen} onClose={handleClose} size="2xl">
        <ModalOverlay bg="rgba(0, 0, 0, 0.6)" backdropFilter="blur(2px)" />
        <ModalContent maxHeight="90vh">
          <ModalHeader borderBottom={'1px solid'} borderColor={'border.default60'}>
            Edit Vendor Details
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody
            pb={0}
            px={0}
            bg={'background.secondary'}
            rounded={'lg'}
            maxHeight="60vh"
            overflowY="auto"
            css={{
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'gray.300',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'gray.400',
              },
            }}
          >
            <form onSubmit={handleSubmit(onSubmit)} id="vendor-edit-form">
              <VStack spacing={6} align="stretch">
                <VStack px={6} spacing={4} align="stretch">
                  <FormControl isInvalid={!!errors.name}>
                    <FormLabel
                      color={errors.name ? 'status.error' : 'text.secondary'}
                      fontSize={'sm'}
                      fontFamily={'primary'}
                      fontWeight={'normal'}
                      mb={'6px'}
                    >
                      Shop Name <Asterisk />
                    </FormLabel>
                    <Input
                      bgColor={'#FFFFFF'}
                      placeholder="Enter shop name"
                      {...register('name')}
                    />
                    <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.email}>
                    <FormLabel
                      color={errors.email ? 'status.error' : 'text.secondary'}
                      fontSize={'sm'}
                      fontFamily={'primary'}
                      fontWeight={'normal'}
                      mb={'6px'}
                    >
                      Email Address <Asterisk />
                    </FormLabel>
                    <Input
                      bgColor={'#FFFFFF'}
                      placeholder="Enter your email"
                      {...register('email')}
                      isDisabled
                      isReadOnly
                    />
                    <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.telephone}>
                    <FormLabel
                      color={errors.telephone ? 'status.error' : 'text.secondary'}
                      fontSize={'sm'}
                      fontFamily={'primary'}
                      fontWeight={'normal'}
                      mb={'6px'}
                    >
                      Business Contact <Asterisk />
                    </FormLabel>
                    <Input
                      bgColor={'#FFFFFF'}
                      placeholder="Eg: 9849********"
                      {...register('telephone')}
                    />
                    <FormErrorMessage>{errors.telephone?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.company_address}>
                    <AddressFieldWithMap
                      name="company_address"
                      label="Company Address"
                      isRequired={false}
                      isGoogleMapsLoaded={isGoogleMapsLoaded}
                    />
                    <FormErrorMessage>{errors.company_address?.message}</FormErrorMessage>
                  </FormControl>

                  <Controller
                    name="certificate_image"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl isInvalid={!!error}>
                        <FormLabel
                          color={error ? 'status.error' : 'text.secondary'}
                          fontSize={'sm'}
                          fontFamily={'primary'}
                          fontWeight={'normal'}
                          mb={'6px'}
                        >
                          Company Certificate
                        </FormLabel>
                        <ImageUpload
                          htmlFor="certificate_image"
                          onChange={(file) => {
                            field.onChange(file);
                          }}
                          initialImage={initialData.certificate_image_url || null}
                          required={false}
                        />
                        <FormErrorMessage>{error?.message}</FormErrorMessage>
                      </FormControl>
                    )}
                  />

                  <Controller
                    name="pan_vat_certificate_image"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl isInvalid={!!error} mb={4}>
                        <FormLabel
                          color={error ? 'status.error' : 'text.secondary'}
                          fontSize={'sm'}
                          fontFamily={'primary'}
                          fontWeight={'normal'}
                          mb={'6px'}
                        >
                          Pan/VAT Certificate
                        </FormLabel>
                        <ImageUpload
                          htmlFor="pan_vat_certificate_image"
                          onChange={(file) => {
                            field.onChange(file);
                          }}
                          initialImage={initialData.pan_vat_certificate_image_url || null}
                          required={false}
                        />
                        <FormErrorMessage>{error?.message}</FormErrorMessage>
                      </FormControl>
                    )}
                  />
                </VStack>
              </VStack>
            </form>
          </ModalBody>

          <ModalFooter
            rounded={'lg'}
            bg={'background.white'}
            px={6}
            mx={0}
            justifyContent="space-between"
            borderTop={'1px solid'}
            borderColor={'border.default60'}
          >
            <Button
              variant="outline"
              colorScheme="brand.primary"
              onClick={handleCancelWithReset}
              _hover={{ bg: '#0000' }}
            >
              Cancel
            </Button>
            <Button
              colorScheme="brand.primary"
              type="submit"
              isLoading={methods.formState.isSubmitting}
              form="vendor-edit-form"
            >
              Update
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </FormProvider>
  );
};

const EditModal: React.FC<IEditModalProps> = ({
  isOpen,
  onClose,
  handleCancel,
  handleUpdate,
  initialData,
}) => {
  return (
    <EditModalContent
      isOpen={isOpen}
      onClose={onClose}
      handleCancel={handleCancel}
      handleUpdate={handleUpdate}
      initialData={initialData}
    />
  );
};

export default EditModal;
